import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Search, MapPin, Calendar, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { HERO_SLIDES } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { Link } from "react-router-dom";

const Hero = () => {
  const [active, setActive] = useState(0);
  const [search, setSearch] = useState({ dest: "", date: "", guests: "" });
  const { toast } = useToast();

  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.dest) {
      toast({ title: "Where to?", description: "Please enter a destination to continue." });
      return;
    }
    toast({
      title: "Searching best deals...",
      description: `Finding trips to ${search.dest} for ${search.guests || "you"}.`,
    });
  };

  return (
    <section className="relative min-h-[95vh] w-full overflow-hidden">
      {/* Slides */}
      {HERO_SLIDES.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover scale-105"
            style={{ animation: i === active ? "kenburns 8s ease-out forwards" : "none" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-900/50 to-slate-900/20" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-40 md:pt-48 pb-32">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs font-medium tracking-wide mb-6">
            <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
            {HERO_SLIDES[active].subtitle}
          </div>
          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {HERO_SLIDES[active].title}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
            {HERO_SLIDES[active].description}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-8 text-base shadow-xl shadow-orange-500/30"
            >
              <Link to="/packages">{HERO_SLIDES[active].cta}</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-slate-900 bg-white/5 backdrop-blur"
            >
              <Link to="/destinations">Browse Destinations</Link>
            </Button>
          </div>
        </div>

        {/* Search box */}
        <form
          onSubmit={handleSearch}
          className="mt-14 md:mt-20 max-w-5xl bg-white rounded-2xl shadow-2xl shadow-slate-900/30 p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2"
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50">
            <MapPin className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Destination
              </label>
              <Input
                value={search.dest}
                onChange={(e) => setSearch({ ...search, dest: e.target.value })}
                placeholder="Where to?"
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <Calendar className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Travel Date
              </label>
              <Input
                type="date"
                value={search.date}
                onChange={(e) => setSearch({ ...search, date: e.target.value })}
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <Users className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Guests
              </label>
              <Input
                value={search.guests}
                onChange={(e) => setSearch({ ...search, guests: e.target.value })}
                placeholder="2 Adults"
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>
          <Button
            type="submit"
            className="h-14 md:h-auto md:w-14 md:aspect-square bg-teal-600 hover:bg-teal-700 rounded-xl"
            aria-label="search"
          >
            <Search className="w-5 h-5" />
            <span className="md:hidden ml-2">Search Trips</span>
          </Button>
        </form>
      </div>

      {/* Arrows */}
      <div className="absolute bottom-8 right-8 z-10 flex gap-2">
        <button
          onClick={() => setActive((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
          className="w-11 h-11 rounded-full bg-white/10 backdrop-blur border border-white/30 text-white hover:bg-white hover:text-slate-900 transition"
          aria-label="prev"
        >
          <ChevronLeft className="w-5 h-5 mx-auto" />
        </button>
        <button
          onClick={() => setActive((i) => (i + 1) % HERO_SLIDES.length)}
          className="w-11 h-11 rounded-full bg-white/10 backdrop-blur border border-white/30 text-white hover:bg-white hover:text-slate-900 transition"
          aria-label="next"
        >
          <ChevronRight className="w-5 h-5 mx-auto" />
        </button>
      </div>

      {/* Dots */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === active ? "w-10 bg-orange-400" : "w-2 bg-white/50"
            }`}
            aria-label={`slide-${i}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes kenburns {
          from { transform: scale(1.05); }
          to { transform: scale(1.15); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
